import { z } from 'zod';
import type { PushCounts, PushMetricDefinition } from './push-analytics.ts';

/**
 * Campaign analytics page (BB-104, AN-002): KPIs with their formula and denominator (DEC-017), a time series, a split by
 * channel / variant / platform / locale, Message Performance per channel, control apart from the KPIs, conversions
 * and API ids. Counts come live from `message_fact` (BB-132) on the send day of each fact (`cohort_day`) — hourly
 * series use the hour the fact happened — and from conversion windows (BB-103) by the day / hour they opened. Test
 * facts never count; control facts only in `control`.
 *
 * Read-only settings come from `GET /campaigns/:id` (`published.content`), «Changes since last viewed» from
 * `GET /objects/campaign/:id/changes` (BB-16), the estimated audience from `POST /audience/estimate` (BB-44), variant
 * statistics from `GET /campaigns/:id/variants-stats` (BB-136).
 */
export const CAMPAIGN_ANALYTICS_GRANULARITIES = ['day', 'hour'] as const;
export const CAMPAIGN_ANALYTICS_BREAKDOWNS = ['channel', 'variant', 'platform', 'locale'] as const;
export type CampaignAnalyticsGranularity = (typeof CAMPAIGN_ANALYTICS_GRANULARITIES)[number];
export type CampaignAnalyticsBreakdown = (typeof CAMPAIGN_ANALYTICS_BREAKDOWNS)[number];
/** Hourly series cover at most this many days. */
export const CAMPAIGN_ANALYTICS_HOURLY_MAX_DAYS = 31;

const Day = z.iso.date();
const days = (q: { from: string; to: string }) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000;

export const CampaignAnalyticsQuery = z
  .object({
    from: Day,
    to: Day,
    granularity: z.enum(CAMPAIGN_ANALYTICS_GRANULARITIES).default('day'),
    breakdown: z.enum(CAMPAIGN_ANALYTICS_BREAKDOWNS).default('channel'),
  })
  .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine((q) => days(q) <= 366, { message: 'at most 366 days', path: ['to'] })
  .refine((q) => q.granularity !== 'hour' || days(q) < CAMPAIGN_ANALYTICS_HOURLY_MAX_DAYS, { message: `hourly series cover at most ${CAMPAIGN_ANALYTICS_HOURLY_MAX_DAYS} days`, path: ['granularity'] });
export type CampaignAnalyticsQuery = z.infer<typeof CampaignAnalyticsQuery>;

/** Raw counts of one slice (the total, a bucket, a breakdown row); every KPI is a formula over them. */
export interface CampaignCounts {
  /** Push sends (handed to FCM/APNs, as BB-79) + cards created + in-app impressions + email/SMS/webhook handed to the sender. */
  messagesSent: number;
  /** Distinct users a message was handed to, a card was created for or an in-app message was shown to. */
  uniqueRecipients: number;
  pushSends: number;
  pushAccepted: number;
  pushDirectOpens: number;
  pushInfluencedOpens: number;
  cardsCreated: number;
  cardImpressions: number;
  cardClicks: number;
  iamImpressions: number;
  iamClicks: number;
  /** Card and in-app clicks (push taps are `pushDirectOpens`). */
  clicks: number;
  /** Distinct users with a conversion window that opened in the slice; null where windows cannot be split so (platform, locale). */
  conversionRecipients: number | null;
  /** Distinct users of those windows who converted on the primary conversion. */
  conversions: number | null;
}

export const CAMPAIGN_KPI_KEYS = [
  'messages_sent',
  'unique_recipients',
  'push_accepted',
  'push_direct_opens',
  'push_influenced_opens',
  'card_impressions',
  'iam_impressions',
  'clicks',
  'conversions',
  'conversion_rate',
] as const;
export type CampaignKpiKey = (typeof CAMPAIGN_KPI_KEYS)[number];

export interface Localized {
  uk: string;
  en: string;
}

export interface CampaignKpi {
  key: CampaignKpiKey;
  title: Localized;
  format: 'count' | 'percent';
  /** A count, or a fraction 0..1; a rate without a denominator is 0 with `explanation` (DEC-017). */
  value: number;
  numerator: number;
  denominator: number | null;
  /** What the number counts and over what, for the tooltip. */
  formula: Localized;
  /** The formula as counts. */
  expression: string;
  /** Where the definition is documented. */
  definitionUrl: string;
  /** Channel-specific KPIs; shown only for campaigns that use the channel. */
  channel?: 'push' | 'content_card' | 'in_app';
  explanation?: { code: 'no_denominator' } & Localized;
}

export interface CampaignAnalyticsSlice {
  key: string | null;
  counts: CampaignCounts;
  /** KPI values of the slice (rates are fractions, null without a denominator or where the slice has no such count). */
  metrics: Record<CampaignKpiKey, number | null>;
}

/** A Message Performance row: one channel (push: one platform), its own counts and metrics. */
export interface CampaignChannelRow {
  channel: 'push' | 'content_card' | 'in_app' | 'email' | 'sms' | 'webhook';
  platform: string | null;
  counts: Record<string, number> | PushCounts;
  metrics: Record<string, number | null>;
}

export interface ChannelMetricDefinition {
  key: string;
  title: Localized;
  format: 'count' | 'percent';
  formula: Localized;
}

export interface CampaignConversionResult {
  id: string;
  kind: 'custom_event' | 'session_start';
  event?: string;
  windowSeconds: number;
  primary: boolean;
  /** Distinct converted users among the recipients with a window (control excluded). */
  converted: number;
  rate: number | null;
}

export interface CampaignAnalytics {
  campaign: { id: string; apiId: string; name: string; status: string; deliveryType: string; category: string; channels: string[]; publishedRevision: number | null; publishedAt: string | null };
  from: string;
  to: string;
  timezone: string;
  granularity: CampaignAnalyticsGranularity;
  breakdown: CampaignAnalyticsBreakdown;
  kpis: CampaignKpi[];
  /** Every bucket of the period (`YYYY-MM-DD`, or `YYYY-MM-DDTHH:00` local time), empty ones included. */
  series: Array<{ bucket: string; counts: CampaignCounts }>;
  /** The split chosen by `breakdown` (control excluded; `key` null for facts without the dimension). */
  rows: CampaignAnalyticsSlice[];
  /** Message Performance; push rows reuse BB-79 counts and metrics. */
  byChannel: CampaignChannelRow[];
  channelMetrics: { push: PushMetricDefinition[]; content_card: ChannelMetricDefinition[]; in_app: ChannelMetricDefinition[]; other: ChannelMetricDefinition[] };
  /** The control group apart from the KPIs: users assigned, windows from assignment and their conversions. */
  control: { assigned: number; conversionRecipients: number; conversions: number; conversionRate: number | null } | null;
  /** All conversions of the published revision (or the default), primary first. */
  conversions: CampaignConversionResult[];
  apiIds: { campaign: string; variants: Array<{ apiId: string; name: string }>; control: string | null };
  /** Live numbers: when they were computed and the newest fact they include. */
  dataWatermark: { generatedAt: string; lastFactRecordedAt: string | null };
}

